import { useState, useEffect, useRef, useMemo } from "react";
import CustomButton from '../common/CustomButton';
import { HiOutlineCode, HiOutlineViewGrid } from "react-icons/hi";
import { notify } from 'react-notify-toast';

interface StateMappingComponentProps {
    localMappings: Record<string, any>;
    setLocalMappings: (mappings: Record<string, any>) => void;
    isJsonView: boolean;
    setIsJsonView: (isJsonView: boolean) => void;
    jsonInput: string;
    setJsonInput: (jsonInput: string) => void;
}

const StateMappingComponent = ({
    localMappings,
    setLocalMappings,
    isJsonView,
    setIsJsonView,
    jsonInput,
    setJsonInput
}: StateMappingComponentProps) => {
    const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
    const [currentTeamInProjectIndex, setCurrentTeamInProjectIndex] = useState(0);
    const [focusedInput, setFocusedInput] = useState<string | null>(null);
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const teamScrollContainerRef = useRef<HTMLDivElement>(null);

    // Memoized project and team data to avoid repetition
    const projectsData = useMemo(() => {
        const projectKeys = Object.keys(localMappings);
        const currentProjectKey = projectKeys[currentTeamIndex];
        const currentProject = currentProjectKey ? localMappings[currentProjectKey] || {} : {};
        const teamKeys = Object.keys(currentProject);
        const currentTeamKey = teamKeys[currentTeamInProjectIndex];
        
        return {
            projectKeys,
            currentProjectKey,
            currentProject,
            teamKeys,
            currentTeamKey,
            isLastProject: currentTeamIndex >= projectKeys.length - 1,
            isLastTeamInProject: currentTeamInProjectIndex >= teamKeys.length - 1,
            hasProjects: projectKeys.length > 0,
            hasTeams: teamKeys.length > 0
        };
    }, [localMappings, currentTeamIndex, currentTeamInProjectIndex]);

    // Reset team index when project changes
    useEffect(() => {
        setCurrentTeamInProjectIndex(0);
    }, [currentTeamIndex]);

    // Handle scrolling when team changes
    useEffect(() => {
        if (teamScrollContainerRef.current) {
            const selectedTeamElement = document.getElementById(`team-${currentTeamInProjectIndex}`);
            if (selectedTeamElement) {
                // Calculate the scroll position to center the selected team
                const container = teamScrollContainerRef.current;
                const containerWidth = container.offsetWidth;
                const teamElementLeft = selectedTeamElement.offsetLeft;
                const teamElementWidth = selectedTeamElement.offsetWidth;
                
                // Center the selected team in the container
                const scrollPosition = teamElementLeft - (containerWidth / 2) + (teamElementWidth / 2);
                
                // Smooth scroll to the position
                container.scrollTo({
                    left: scrollPosition,
                    behavior: 'smooth'
                });
            }
        }
    }, [currentTeamInProjectIndex]);

    // Maintain focus after state updates
    useEffect(() => {
        if (focusedInput && inputRefs.current[focusedInput]) {
            inputRefs.current[focusedInput]?.focus();
        }
    }, [localMappings, focusedInput]);

    const handlePreviousTeam = () => {
        if (currentTeamIndex > 0) {
            setCurrentTeamIndex(currentTeamIndex - 1);
        }
    };

    const handleNextTeam = () => {
        if (currentTeamIndex < projectsData.projectKeys.length - 1) {
            setCurrentTeamIndex(currentTeamIndex + 1);
        }
    };

    const handlePercentageChange = (projectKey: string, teamKey: string, stateName: string, value: string, inputId: string) => {
        // Set the currently focused input
        setFocusedInput(inputId);
        
        // Create a deep copy of the mappings
        const updatedMappings = {...localMappings};
        
        if (!updatedMappings[projectKey]) {
            updatedMappings[projectKey] = {};
        }
        
        if (!updatedMappings[projectKey][teamKey]) {
            updatedMappings[projectKey][teamKey] = {};
        }
        
        // Allow empty string for better UX during typing
        if (value === '') {
            updatedMappings[projectKey][teamKey][stateName] = '';
            setLocalMappings(updatedMappings);
            return;
        }
        
        // Remove non-numeric characters
        const numericValue = value.replace(/\D/g, '');
        
        // Parse to number if possible
        let numValue = numericValue === '' ? 0 : parseInt(numericValue);
        
        // Cap at 100
        if (numValue > 100) numValue = 100;
        
        // Update the value
        updatedMappings[projectKey][teamKey][stateName] = numValue;
        
        setLocalMappings(updatedMappings);
    };

    const handleInputFocus = (inputId: string) => {
        setFocusedInput(inputId);
    };

    const handleInputBlur = () => {
        setFocusedInput(null);
    };

    const handlePreviousTeamInProject = () => {
        if (currentTeamInProjectIndex > 0) {
            setCurrentTeamInProjectIndex(currentTeamInProjectIndex - 1);
        }
    };

    const handleNextTeamInProject = () => {
        if (!projectsData.currentProjectKey) return;
        
        if (currentTeamInProjectIndex < projectsData.teamKeys.length - 1) {
            setCurrentTeamInProjectIndex(currentTeamInProjectIndex + 1);
        }
    };
    
    const handleNextProject = () => {
        if (currentTeamIndex < projectsData.projectKeys.length - 1) {
            setCurrentTeamIndex(currentTeamIndex + 1);
            setCurrentTeamInProjectIndex(0); // Reset team index when changing projects
        }
    };

    const handleToggleView = () => {
        if (isJsonView) {
            // When switching from JSON to UI view, try to parse the JSON
            try {
                const parsedJson = JSON.parse(jsonInput);
                setLocalMappings(parsedJson);
                setCurrentTeamIndex(0); // Reset team index
                setCurrentTeamInProjectIndex(0); // Reset team in project index
                setIsJsonView(false);
            } catch (error) {
                notify.show('Invalid JSON format. Please fix before switching views.', "error", 3000);
            }
        } else {
            // Switching to JSON view - update the JSON with current UI state
            setJsonInput(JSON.stringify(localMappings, null, 2));
            setIsJsonView(true);
        }
    };

    const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setJsonInput(e.target.value);
    };

    // Function to render navigation buttons with consistent styling
    const renderNavigationButton = (
        onClick: () => void,
        label: string,
        disabled = false,
        isPrimary = true
    ) => {
        
        
        const variantClass = isPrimary 
            ? (!disabled ? " hover:from-blue-700/95 hover:to-blue-500/95 hover:scale-105 hover:shadow-lg border-0" 
                        : "bg-gradient-to-r from-gray-400/90 to-gray-300/90 cursor-not-allowed opacity-50 border-0")
            : (!disabled ? "hover:bg-gray-100/80 dark:hover:bg-gray-700/80 hover:scale-105 border border-gray-200/80 dark:border-gray-700/80" 
                        : "opacity-50 cursor-not-allowed border border-gray-200/80 dark:border-gray-700/80");
        
        return (
            <CustomButton
                onClick={onClick}
                variant={isPrimary ? "primary" : "cancel"}
                size="md"
                disabled={disabled}
                className={` ${variantClass}`}
            >
                {label}
            </CustomButton>
        );
    };

    return (
        <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <button 
                            onClick={handleToggleView}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-blue-500/30 hover:bg-blue-500/40 text-blue-700 dark:text-white transition-all border border-blue-400/30 mr-4"
                        >
                            {isJsonView ? (
                                <>
                                    <HiOutlineViewGrid className="w-4 h-4" />
                                    <span>UI View</span>
                                </>
                            ) : (
                                <>
                                    <HiOutlineCode className="w-4 h-4" />
                                    <span>JSON View</span>
                                </>
                            )}
                        </button>
                        {!isJsonView && (
                            <>
                                <button 
                                    onClick={handlePreviousTeam}
                                    disabled={currentTeamIndex === 0 || !projectsData.hasProjects}
                                    className={`flex items-center justify-center w-8 h-8 rounded-full mr-3 ${
                                        currentTeamIndex === 0 || !projectsData.hasProjects
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
                                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-800/70'
                                    } transition-all`}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                                    </svg>
                                </button>
                                <div className="flex flex-col">
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">PROJECT</span>
                                    <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400 bg-clip-text text-transparent">
                                        {projectsData.hasProjects 
                                            ? projectsData.currentProjectKey 
                                            : "No projects available"}
                                    </h2>
                                </div>
                            </>
                        )}
                        {isJsonView && (
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">JSON</span>
                                <h2 className="text-lg font-bold text-gray-800 dark:text-white">State Mappings Configuration</h2>
                            </div>
                        )}
                    </div>
                    {!isJsonView && (
                        <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300 px-3 py-1 rounded-full bg-gray-50 dark:bg-gray-800 mr-3">
                                {projectsData.hasProjects 
                                    ? `${currentTeamIndex + 1} of ${projectsData.projectKeys.length}` 
                                    : "0 of 0"}
                            </span>
                            <button 
                                onClick={handleNextTeam}
                                disabled={projectsData.isLastProject || !projectsData.hasProjects}
                                className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                    projectsData.isLastProject || !projectsData.hasProjects
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
                                    : 'bg-blue-100 text-blue-600 hover:bg-blue-200 dark:bg-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-800/70'
                                } transition-all`}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {isJsonView ? (
                /* JSON Editor View */
                <div className="p-4 bg-white dark:bg-gray-800">
                    <div className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                        Paste your JSON configuration below. Ensure it follows the correct format for state mappings.
                    </div>
                    <textarea
                        value={jsonInput}
                        onChange={handleJsonChange}
                        className="w-full h-[400px] p-3 rounded-md font-mono text-sm border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:border-transparent bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 overflow-auto"
                        placeholder="Paste your JSON configuration here..."
                    />
                </div>
            ) : (
                /* UI State Mapping View */
                <>
                    {/* Team Selection */}
                    <div className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-750 p-4 border-b border-gray-100 dark:border-gray-700">
                        <div className="flex flex-col">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">Team</span>
                            <div className="relative overflow-x-auto pb-1 pt-1">
                                <div 
                                    ref={teamScrollContainerRef}
                                    className="flex items-center space-x-2 pb-1 overflow-x-auto scrollbar-thin scrollbar-thumb-blue-300 dark:scrollbar-thumb-blue-700 scrollbar-track-transparent"
                                >
                                    {(() => {
                                        return projectsData.teamKeys.map((teamName, idx) => {
                                            const isCurrentTeam = idx === currentTeamInProjectIndex;
                                            
                                            return (
                                                <button 
                                                    id={`team-${idx}`}
                                                    key={teamName} 
                                                    className={`px-3 py-2 rounded-md transition-all text-sm ${
                                                        isCurrentTeam
                                                        ? 'bg-blue-600 text-white font-medium' 
                                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-700'
                                                    } border border-gray-200 dark:border-gray-700 whitespace-nowrap`}
                                                    onClick={() => setCurrentTeamInProjectIndex(idx)}
                                                >
                                                    {teamName}
                                                </button>
                                            );
                                        });
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* States Mapping Content */}
                    <div className="p-5 bg-white dark:bg-gray-800">
                        {(() => {
                            try {
                                if (!projectsData.currentProjectKey) return <div className="p-6 text-center text-gray-500">No project selected</div>;
                                
                                if (!projectsData.hasTeams) return <div className="p-6 text-center text-gray-500">No teams found for this project</div>;

                                const currentTeamKey = projectsData.teamKeys[currentTeamInProjectIndex];
                                const stateData = projectsData.currentProject[currentTeamKey] || {};
                                
                                return (
                                    <div>
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">State Mappings</h3>
                                            <span className="text-sm text-gray-500 dark:text-gray-400">Set completion percentages</span>
                                        </div>
                                        
                                        <div className="space-y-3 min-h-[280px] max-h-[280px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-blue-300 dark:scrollbar-thumb-blue-700 scrollbar-track-transparent hover:scrollbar-thumb-blue-400 dark:hover:scrollbar-thumb-blue-600">
                                            {Object.entries(stateData).length > 0 ? (
                                                Object.entries(stateData).map(([stateName, completionValue], stateIndex) => {
                                                    const inputId = `${projectsData.currentProjectKey}-${currentTeamKey}-${stateName}`;
                                                    
                                                    return (
                                                        <div 
                                                            key={stateIndex} 
                                                            className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-blue-50/80 to-blue-50/30 dark:from-gray-800/90 dark:to-gray-750/80 hover:from-blue-100/80 hover:to-blue-50/50 dark:hover:from-blue-900/30 dark:hover:to-blue-800/40 transition-all duration-200 group border border-blue-100/40 dark:border-gray-700 hover:border-blue-200/70 dark:hover:border-blue-700/50"
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-blue-400/80 to-blue-500/90 text-white text-xs font-medium group-hover:from-blue-500/90 group-hover:to-blue-600 group-hover:scale-110 dark:from-blue-500/70 dark:to-blue-600/80 shadow-sm group-hover:shadow-md transition-all duration-200">
                                                                    {stateIndex + 1}
                                                                </div>
                                                                <span className="font-medium text-gray-700 dark:text-gray-200 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                                                                    {stateName}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <input
                                                                    type="text"
                                                                    ref={el => inputRefs.current[inputId] = el}
                                                                    value={completionValue === '' ? '' : String(completionValue).replace(/%/g, '')}
                                                                    onChange={(e) => 
                                                                        handlePercentageChange(
                                                                            projectsData.currentProjectKey,
                                                                            currentTeamKey,
                                                                            stateName,
                                                                            e.target.value,
                                                                            inputId
                                                                        )
                                                                    }
                                                                    onFocus={() => handleInputFocus(inputId)}
                                                                    onBlur={handleInputBlur}
                                                                    className="w-16 px-2 py-1 mr-1 text-center rounded-md border border-blue-200 dark:border-blue-700 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600 focus:border-transparent dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                                                    maxLength={3}
                                                                    placeholder="0"
                                                                />
                                                                <div className="px-2 py-1 rounded-lg bg-gradient-to-r from-blue-400/90 to-blue-500/80 text-white text-sm font-medium group-hover:from-blue-500 group-hover:to-blue-600 transition-all shadow-sm group-hover:shadow-md">
                                                                    %
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="flex items-center justify-center h-[280px]">
                                                    <div className="text-center p-6 rounded-xl bg-gray-50/50 dark:bg-gray-800/30 border border-gray-100/60 dark:border-gray-700/40">
                                                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-500 dark:text-blue-400 mb-3">
                                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                                            </svg>
                                                        </div>
                                                        <p className="text-gray-500 dark:text-gray-400">No state mappings available for this team</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            } catch (err) {
                                console.error('Error rendering team states:', err);
                                return (
                                    <div className="p-6 text-center text-gray-500">
                                        Unable to display states for this project/team.
                                    </div>
                                );
                            }
                        })()}
                    </div>

                    {/* State Mapping Footer Controls */}
                    <div className="border-t border-gray-200 dark:border-gray-700 py-4 px-5">
                        {(() => {
                            if (!projectsData.currentProjectKey) return null;
                            
                            // Determine which button to show based on current position
                            let actionButton;
                            if (!projectsData.isLastTeamInProject) {
                                actionButton = renderNavigationButton(
                                    handleNextTeamInProject,
                                    "Next Team"
                                );
                            } else if (!projectsData.isLastProject) {
                                actionButton = renderNavigationButton(
                                    handleNextProject,
                                    "Next Project"
                                );
                            } else {
                                actionButton = renderNavigationButton(
                                    () => {}, // No-op function
                                    "Next Team",
                                    true // Disabled
                                );
                            }
                            
                            return (
                                <div className="w-full flex justify-between items-center">
                                    {renderNavigationButton(
                                        handlePreviousTeamInProject,
                                        "Previous Team",
                                        currentTeamInProjectIndex === 0,
                                        false // Not primary
                                    )}
                                    
                                    <div className="flex items-center justify-center space-x-2">
                                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                                            {currentTeamInProjectIndex + 1} of {projectsData.teamKeys.length}
                                        </span>
                                    </div>
                                    
                                    {actionButton}
                                </div>
                            );
                        })()}
                    </div>
                </>
            )}
        </div>
    );
};

export default StateMappingComponent; 